import * as _ from 'underscore';

import { SiteUrls } from '@tableau/shared-urls';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import { watchUrlForBreadcrumbUpdates } from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { BreadcrumbStateInfo } from '../../app/content/breadcrumbs/BreadcrumbStateInfo';
import { SiteUsersGrid } from '../../app/content/SiteUsersGrid';
import { IMainScope } from '../../app/IMainScope';
import { IPlaceTitleScope } from '../../app/IPlaceTitleScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { detachSorterOnPathChange } from '../../app/spaces/SpaceConfig/SpaceConfigHelper';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ListResult } from '../fetchers/ListResult';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ActionContext } from '../helpers/ActionContext';
import { SiteRoleFilterHelper } from '../helpers/SiteRoleFilterHelper';
import { SiteUserContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { ITabsBannerModel } from '../models/ITabsBannerModel';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResourceQuery } from '../resources/ResourceQuery';
import { SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { SiteUsers } from '../resources/SiteUsers';
import { ILicenseConsumptionInfo } from '../ServerApi/types/ILicenseConsumptionInfo';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { IActionButton, IListActionButton } from '../services/ActionPanelService';
import { SyncGroupEvent } from '../services/ActiveDirectoryGroupAction';
import { AddUsersOptionsAction, UserAddedEvent } from '../services/AddUsersOptionsAction';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { FilterPanelService, ISelectFilterDefinition } from '../services/FilterPanelService';
import { LicensesService } from '../services/LicensesService';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ServerService } from '../services/ServerService';
import {
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../services/SiteAuthenticationSettingsService';
import { SiteUsersActions } from '../services/SiteUsersActions';
import { SorterFactory } from '../services/SorterFactory';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { IFilterPanelContainerScope } from './FilterPanelCtrl';
import { SiteRolesEvent } from './SiteMembershipDialogCtrl';

// REMOVE WITH FeatureFlags.stylishSiteUsers

export interface ISiteUsersPlaceScope extends
  IActionsMenuScope<ISiteUserInfo, {}>,
  IMainScope,
  IPlaceTitleScope,
  IFilterPanelContainerScope,
  ListActionsScope,
  RefreshContentScope,
  SubplaceWithActionButtonsScope<ISiteUserInfo, {}>,
  SubplaceWithDataGridScope<ISiteUserInfo>,
  IFilterPanelContainerScope
{
  model: ITabsBannerModel;
  multisite: boolean;
  server: ServerService;
  licenseConsumptionInfo: ILicenseConsumptionInfo;
  shouldShowSubscriptionLicensingInfoBar: boolean;
  isFetchingLicenseInfo: boolean;
  isCoreBasedLicense: boolean;
}

export function getSiteUsersPlaceBreadcrumb(multiSite: boolean): BreadcrumbStateInfo {
  const siteUrls = siteUrlsSelector(appStore.getState());
  return {
    id: siteUrls && siteUrls.usersUrl(),
    text: multiSite ? Messages.allSiteUsers() : Messages.allUsers(),
    href: siteUrls && siteUrls.usersUrl(),
    testId: 'site-users-link'
  };
}

export class SiteUsersPlaceCtrl {

  public static $inject = [
    '$location',
    '$scope',
    '$translate',
    'AngularToReactDataGridBridge',
    'AddUsersOptionsAction',
    'BrowserTitleService',
    'QueryService',
    'ServerService',
    'SiteRoles',
    'canSessionUserManageUsers', // from state router
    'siteAuthenticationSettings', // injected by state router
    'SiteAuthenticationSettingsService',
    'SiteUsers',
    'SiteUsersActions',
    'SorterFactory',
    'FilterPanelService',
    'LicensesService',
    'SiteRoleOptionsResource'
  ];

  private siteRoleFilterDefinition: ISelectFilterDefinition<string>;

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'usersEmptyMessage' };

  constructor(
    private $location: ng.ILocationService,
    private $scope: ISiteUsersPlaceScope,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    addUsersOptionsAction: AddUsersOptionsAction,
    BrowserTitleService: BrowserTitleService,
    private queryService: QueryService,
    private serverService: ServerService,
    private siteRoles: SiteRolesResource,
    canSessionUserManageUsers: boolean,
    private siteAuthenticationSettings: ISiteAuthenticationSettings,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
    private users: SiteUsers,
    siteUsersActions: SiteUsersActions,
    sorterFactory: SorterFactory,
    filterPanelService: FilterPanelService,
    private licensesService: LicensesService,
    private siteRoleOptionsResource: SiteRoleOptionsResource
  ) {
    $scope.multisite = serverService.isSessionMultiSite();
    BrowserTitleService.setTitleWithPlace({translatablePlaceName: $scope.multisite ? 'siteUsers' : 'users'});

    $scope.model = { itemsCount: 0 };
    $scope.isFetchingLicenseInfo = false;
    $scope.shouldShowSubscriptionLicensingInfoBar = false;
    $scope.server = serverService;
    $scope.sorter = sorterFactory.create(SiteUserContentType.defaultOrder);
    $scope.$on('$destroy', () => $scope.sorter.detach());
    $scope.selectedItems = new Set<ISiteUserInfo>((user: ISiteUserInfo) => user.id);

    const actions = siteUsersActions.getSiteUsersActionsButtons();
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, $scope.selectedItems);

    this.configureForReactDataGrid(actions, getActionTarget, siteRoleOptionsResource);

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    $scope.listActions = [{
      name: 'add-users',
      translate: 'addUsers',
      isAvailable: () => canSessionUserManageUsers,
      execute: ($event: JQueryEventObject) => {
        addUsersOptionsAction.showAddSiteUsersOptionsDialog(ActionContext.fromEvent($event));
      }
    }];
    $scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    $scope.filterControls = {};

    $scope.filterDefinitions = filterPanelService.getFilterDefinitions([ 'siteRole' ]);
    this.siteRoleFilterDefinition = <ISelectFilterDefinition<string>>$scope.filterDefinitions[0];

    const queryUpdater = this.createQueryUpdater();

    $scope.sliceChanged = (newSlice: ListResult<ISiteUserInfo>) => {
      $scope.selectedItems.updateItems(newSlice.items);
      $scope.model.itemsCount = newSlice.total;
    };

    $scope.refreshContent = () => {
      queryUpdater.refresh();
      this.updateSiteLicenseConsumptionInfo();
    };

    $scope.refreshContent();

    $scope.placeTitleClicked = () => {
      $scope.refreshContent();
    };

    $scope.$on(UserAddedEvent, () => {
      $scope.refreshContent();
    });

    $scope.$on(SiteRolesEvent.SiteRolesChanged, () => {
      $scope.refreshContent();
    });

    $scope.$on(SyncGroupEvent.groupSynced, () => {
      $scope.refreshContent();
    });

    $scope.emptyTemplateModel = SiteUsersPlaceCtrl.emptyTemplateModel;

    this.initBreadcrumbs();

    const currentPath = this.$location.path();
    const shouldDetach = () => this.$location.path() !== currentPath;
    detachSorterOnPathChange(this.$scope, this.$location, shouldDetach);
  }

  private updateSiteRoleCounts(baseQuery: IResourceQuery<ISiteUserInfo>) {
    this.users.getUserCountsBySiteRole(baseQuery).then((userCountsByRole: _.Dictionary<number>) => {
      const allSiteRolesExceptGuest = _.without(this.siteRoles.allOptions(), SiteRoles.Guest);
      this.siteRoleFilterDefinition.options =
        SiteRoleFilterHelper.siteRoleOptions(allSiteRolesExceptGuest, this.siteRoleOptionsResource, userCountsByRole);
    });
  }

  private getUserAuthenticationInfo(user: ISiteUser): {name: string, isEnabled: boolean} {
    const provider = this.siteAuthenticationSettingsService.findProvider(this.siteAuthenticationSettings, user.authenticationProviderId);
    if (provider) {
      return {
        name: this.siteAuthenticationSettingsService.getProviderDisplayName(provider),
        isEnabled: provider.isEnabled
      };
    } else {
      return {
        name: this.$translate.instant('unspecified'),
        isEnabled: false
      };
    }
  }

  private updateSiteLicenseConsumptionInfo = () => {
    this.licensesService.hasSiteLicensingConsumptionInfo().then((hasSiteLicensingInfo: boolean) => {
      const showLicenseInfoBarForSingleSiteServer = !this.serverService.isSessionMultiSite() && this.serverService.shouldShowServerLicensingTierInfo();
      if ((hasSiteLicensingInfo === true) || showLicenseInfoBarForSingleSiteServer) {
        this.$scope.shouldShowSubscriptionLicensingInfoBar = true;
        this.$scope.isFetchingLicenseInfo = true;
        const licensingCapacityInfoPromise = (hasSiteLicensingInfo === true) ? this.licensesService.fetchSiteScopedCapacityInfoForInfoBar() : this.licensesService.fetchCapacityInfoForInfoBar();
        this.processLicenseConsumptionInfo(licensingCapacityInfoPromise);
      }
    });
  }

  private processLicenseConsumptionInfo(licensingCapacityInfoPromise: ng.IPromise<ILicenseConsumptionInfo>){
    licensingCapacityInfoPromise.then((licenseInfo: ILicenseConsumptionInfo) => {
      this.$scope.licenseConsumptionInfo = licenseInfo;
      this.$scope.isCoreBasedLicense = licenseInfo.totalCoreLicenses > 0;
    }).finally(() => {
      this.$scope.isFetchingLicenseInfo = false;
    });
  }

  private configureForReactDataGrid(actions: IActionButton<ISiteUserInfo, {}>[],
                                    getActionTarget: () => IActionTarget<ISiteUserInfo, {}>,
                                    siteRoleOptionsResource: SiteRoleOptionsResource) {
    const columns = SiteUsersGrid.columns({
      actions: actions,
      getActionTarget: getActionTarget,
      getUserAuthenticationInfo: (user: ISiteUser) => this.getUserAuthenticationInfo(user),
      scope: this.$scope,
      showAuthenticationColumn: this.siteAuthenticationSettingsService.isEnabled(),
      showDomainNameColumn: this.serverService.isServerDomainFamilyActiveDirectory(),
      siteUrls: new SiteUrls({siteUrlName: this.serverService.sessionSiteUrlName(), queryString: window.location.search}),
      translate: (key: string) => this.$translate.instant(key),
      siteRoleOptionsResource: siteRoleOptionsResource
    });

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(SiteUsersPlaceCtrl.emptyTemplateModel),
      canShowFilterPanel: SiteUserContentType.canShowFilterPanel,
      headerHeight: CommonStyles.Sizes.DataGridWithPictureIconsHeaderHeight,
      rowOuterHeight: CommonStyles.Sizes.DataGridWithPictureIconsRowHeight
    });
  }

  private createQueryUpdater(): IQueryUpdater {
    return this.queryService.createQueryUpdater(
      this.$scope,
      SiteUserContentType,
      this.users,
      (resourceQuery) => {
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: this.$scope, sliceable: resourceQuery });
        this.$scope.sliceable = resourceQuery;
        this.$scope.sorter.setOrder(resourceQuery.getOrder());
        this.updateSiteRoleCounts(resourceQuery);
        resourceQuery.getCount().then((result: number) => {
          this.$scope.model.itemsCount = result;
        });
      },
      {}
    );
  }

  private initBreadcrumbs() {
    const breadcrumbs: BreadcrumbStateInfo[] = [
      getSiteUsersPlaceBreadcrumb(this.serverService.isSessionMultiSite())
    ];
    appStore.dispatch(updateBreadcrumbs(breadcrumbs));

    const currentPath = this.$location.path();
    watchUrlForBreadcrumbUpdates(this.$scope, this.$location, () => this.$location.path() === currentPath);
  }

}

Module.instance().controller('SiteUsersPlaceCtrl', SiteUsersPlaceCtrl);
